import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { MarkdownService } from '../../../services/shared/markdown.service';
import { PennyDrawerService } from '../../../services/ai/penny-drawer.service';
import {
    PennyFeedbackRating,
    PennyPortalConfig,
    PennyPortalService,
} from '../../../services/ai/penny-portal.service';
import { PennySource } from '../../../services/ai.service';

interface PennyChatMessage {
    role: 'user' | 'assistant';
    content: string;
    html?: string;
    messageId?: number;
    sources?: PennySource[];
    feedback?: PennyFeedbackRating | null;
    copied?: boolean;
    failed?: boolean;
}

const PENNY_ERROR_COPY = 'Sorry — I ran into a problem answering that. Please try again.';

/** Ask Penny drawer for homeowners (SD-9449) — app-root mounted, self-gating, read-only (answers + links only). */
@UntilDestroy()
@Component({
    selector: 'app-penny-drawer-host',
    templateUrl: './penny-drawer-host.component.html',
    styleUrls: ['./penny-drawer-host.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class PennyDrawerHostComponent implements AfterViewChecked, OnDestroy {
    @ViewChild('scrollBody') scrollBody: ElementRef<HTMLElement>;
    @ViewChild('composer') composer: ElementRef<HTMLTextAreaElement>;

    isOpen = false;
    isExpanded = false;
    config: PennyPortalConfig | null = null;

    messages: PennyChatMessage[] = [];
    draft = '';
    inFlight = false;

    welcomeHtml = '';

    readonly quickPrompts: string[] = [
        'How do I pay my balance?',
        'Tell me about our CC&Rs',
        'How do I make a maintenance request?',
        'How do I view my violations?',
    ];

    loadingMessageIndex = 0;
    readonly loadingMessages: string[] = [
        'Thinking about that...',
        'Looking that up for you...',
        'Searching community documents...',
        'Finding the answer...',
        'Almost there...',
    ];

    private readonly _orgName: string;
    private _loadingInterval: ReturnType<typeof setInterval> | null = null;
    private _pendingScrollToUserMessage = false;

    constructor(
            private _drawer: PennyDrawerService,
            private _portal: PennyPortalService,
            private _markdown: MarkdownService,
            private _router: Router,
    ) {
        const jwt = this._drawer.eligible ? LegFiJwtService.read() : null;
        this._orgName = jwt?.orgName || 'your community';
        this._drawer.ensureConfigLoaded();

        this._drawer.config$.pipe(untilDestroyed(this)).subscribe((config) => {
            this.config = config;
            if (config?.enabled) {
                this.welcomeHtml = this._markdown.formatMessage(this._welcomeMessage(config));
            }
        });
        this._drawer.isOpen$.pipe(untilDestroyed(this)).subscribe((open) => {
            this.isOpen = open;
            if (open) {
                setTimeout(() => this.composer?.nativeElement?.focus(), 220);
            }
        });
        this._drawer.mode$.pipe(untilDestroyed(this)).subscribe((mode) => {
            this.isExpanded = mode === 'expanded';
        });
    }

    ngAfterViewChecked(): void {
        if (this._pendingScrollToUserMessage) {
            this._pendingScrollToUserMessage = false;
            this._scrollLastUserMessageIntoView();
        }
    }

    ngOnDestroy(): void {
        this._stopLoadingRotation();
    }

    get widgetEnabled(): boolean {
        return this._drawer.widgetEnabled;
    }

    get title(): string {
        return 'Ask ' + this._pennyName;
    }

    get showCitations(): boolean {
        return this.config?.showSourceCitations !== false;
    }

    get currentLoadingMessage(): string {
        return this.loadingMessages[this.loadingMessageIndex];
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.isOpen) {
            this._drawer.close();
        }
    }

    onClose(): void {
        this._drawer.close();
    }

    onToggleExpand(): void {
        this._drawer.toggleMode();
    }

    onChip(prompt: string): void {
        this.send(prompt);
    }

    onSend(): void {
        this.send(this.draft);
    }

    onComposerKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            this.send(this.draft);
        }
    }

    autoGrow(el: HTMLTextAreaElement): void {
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
    }

    /** Delegated handler: routes rendered /app/... links through the Angular Router. */
    onBodyClick(event: MouseEvent): void {
        const anchor = (event.target as HTMLElement)?.closest?.('a');
        if (!anchor) {
            return;
        }
        const href = anchor.getAttribute('href') || '';
        let path: string | null = null;
        if (href.startsWith('/app/')) {
            path = href;
        } else if (/^https?:/i.test(href)) {
            try {
                const url = new URL(href);
                if (url.origin === window.location.origin && url.pathname.startsWith('/app/')) {
                    path = url.pathname + url.search + url.hash;
                }
            } catch {
                path = null;
            }
        }
        if (path) {
            event.preventDefault();
            void this._router.navigateByUrl(path);
        }
    }

    onCopy(msg: PennyChatMessage): void {
        void navigator.clipboard?.writeText(this._markdown.markdownToPlainText(msg.content));
        msg.copied = true;
        setTimeout(() => {
            msg.copied = false;
        }, 2000);
    }

    onFeedback(msg: PennyChatMessage, rating: PennyFeedbackRating): void {
        const conversationId = this._drawer.conversationId;
        if (!msg.messageId || !conversationId || msg.feedback === rating) {
            return;
        }
        const previous = msg.feedback ?? null;
        msg.feedback = rating;
        this._portal.submitFeedback(conversationId, msg.messageId, rating)
                .pipe(untilDestroyed(this))
                .subscribe({
                    error: () => {
                        msg.feedback = previous;
                    },
                });
    }

    trackByIndex(index: number): number {
        return index;
    }

    private send(message: string): void {
        const trimmed = (message || '').trim();
        if (!trimmed || this.inFlight) {
            return;
        }
        this.messages.push({ role: 'user', content: trimmed });
        this.draft = '';
        this.inFlight = true;
        this._pendingScrollToUserMessage = true;
        this._startLoadingRotation();
        if (this.composer?.nativeElement) {
            this.composer.nativeElement.style.height = 'auto';
        }

        this._portal.sendMessage(trimmed, this._drawer.conversationId ?? undefined)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._drawer.conversationId = res.conversationId;
                        this.messages.push({
                            role: 'assistant',
                            content: res.answer || '',
                            html: this._markdown.formatMessage(res.answer || ''),
                            sources: res.sources || [],
                            feedback: null,
                        });
                        this._finishTurn();
                        this._refreshMessageIds();
                    },
                    error: () => {
                        this.messages.push({
                            role: 'assistant',
                            content: PENNY_ERROR_COPY,
                            html: this._markdown.formatMessage(PENNY_ERROR_COPY),
                            failed: true,
                        });
                        this._finishTurn();
                    },
                });
    }

    private _finishTurn(): void {
        this._stopLoadingRotation();
        this.inFlight = false;
        setTimeout(() => this.composer?.nativeElement?.focus());
    }

    /** The message response carries no messageId — one cheap conversation fetch backfills ids so thumbs can submit. */
    private _refreshMessageIds(): void {
        const conversationId = this._drawer.conversationId;
        if (!conversationId) {
            return;
        }
        this._portal.getConversation(conversationId)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        const serverIds = (res.messages || [])
                                .filter((m) => m.role === 'assistant')
                                .map((m) => m.messageId ?? m.id)
                                .filter((id): id is number => typeof id === 'number');
                        const local = this.messages.filter((m) => m.role === 'assistant' && !m.failed);
                        // Align from the end — the newest local answers are the ones missing ids.
                        for (let offset = 1; offset <= Math.min(serverIds.length, local.length); offset++) {
                            const target = local[local.length - offset];
                            if (!target.messageId) {
                                target.messageId = serverIds[serverIds.length - offset];
                            }
                        }
                    },
                    error: () => {
                        // Thumbs stay disabled for this turn; the next fetch retries.
                    },
                });
    }

    private _startLoadingRotation(): void {
        this.loadingMessageIndex = 0;
        this._stopLoadingRotation();
        this._loadingInterval = setInterval(() => {
            this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.loadingMessages.length;
        }, 2500);
    }

    private _stopLoadingRotation(): void {
        if (this._loadingInterval !== null) {
            clearInterval(this._loadingInterval);
            this._loadingInterval = null;
        }
    }

    private _scrollLastUserMessageIntoView(): void {
        const body = this.scrollBody?.nativeElement;
        if (!body) {
            return;
        }
        const bubbles = body.querySelectorAll<HTMLElement>('.penny-msg.is-user');
        const last = bubbles.length ? bubbles[bubbles.length - 1] : null;
        if (last) {
            body.scrollTop = Math.max(0, last.offsetTop - body.offsetTop - 12);
        }
    }

    private get _pennyName(): string {
        return (this.config?.pennyName || '').trim() || 'Penny';
    }

    private _welcomeMessage(config: PennyPortalConfig): string {
        const custom = (config.welcomeMessage || '').trim();
        if (custom) {
            return custom;
        }
        const named = this._pennyName !== 'Penny' ? this._pennyName + ', ' : '';
        return 'Hi 👋\n\nI\'m ' + named + 'your PayHOA AI assistant, here to help with questions about ' + this._orgName
                + ' and living here.'
                + '\n\nWhat can I help you with today?';
    }
}
